import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MCQ } from '../types/mcq';
import { Button } from './ui/Button';
import { AlertTriangle, Edit2, Lightbulb } from 'lucide-react';
import { TagList } from './TagList';
import { AIExplanation } from './AIExplanation';
import { ToReviewCheckbox } from './ToReviewCheckbox';

interface MCQViewProps {
  mcq: MCQ;
  onEdit: () => void;
  onMarkAsDuplicate: () => void;
  onUpdateMCQ: (mcq: MCQ) => void;
}

export function MCQView({ mcq, onEdit, onMarkAsDuplicate, onUpdateMCQ }: MCQViewProps) {
  const getOptionClass = (option: string, letter: string, answer: string) => {
    const isCorrect = letter === answer;
    return `p-3 sm:p-4 rounded-lg border ${
      isCorrect 
        ? 'border-green-500 bg-green-50 text-green-900'
        : 'border-gray-200 text-gray-700'
    }`;
  };

  const handleToReviewChange = async (checked: boolean) => {
    await onUpdateMCQ({
      ...mcq,
      toReview: checked
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm bg-gray-200 text-gray-600 px-2 py-1 rounded">
            MCQID: {mcq.mcqId}
          </span>
          <ToReviewCheckbox 
            checked={mcq.toReview || false}
            onChange={handleToReviewChange}
          />
        </div>
        <p className="text-base sm:text-lg font-medium text-gray-900">{mcq.question}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className={getOptionClass(mcq.optionA, 'A', mcq.answer)}>
          <span className="font-medium">A.</span> {mcq.optionA}
        </div>
        <div className={getOptionClass(mcq.optionB, 'B', mcq.answer)}>
          <span className="font-medium">B.</span> {mcq.optionB}
        </div>
        <div className={getOptionClass(mcq.optionC, 'C', mcq.answer)}>
          <span className="font-medium">C.</span> {mcq.optionC}
        </div>
        <div className={getOptionClass(mcq.optionD, 'D', mcq.answer)}>
          <span className="font-medium">D.</span> {mcq.optionD}
        </div>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {mcq.takeaway && (
          <div className="w-full bg-yellow-50 p-3 sm:p-4 rounded-lg border border-yellow-100">
            <div className="flex items-center gap-2 text-yellow-800 font-medium mb-3">
              <Lightbulb className="w-5 h-5" />
              <span>Takeaway</span>
            </div>
            <div className="prose prose-sm max-w-none text-yellow-900 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 overflow-x-auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {mcq.takeaway}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {mcq.tags && mcq.tags.length > 0 && (
          <TagList tags={mcq.tags} />
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600">
            <span>{mcq.topic}</span>
            <span>•</span>
            <span>{mcq.subtopic}</span>
          </div>
          <AIExplanation mcq={mcq} onUpdateMCQ={onUpdateMCQ} />
        </div>

        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
          <Button
            onClick={onEdit}
            variant="outline"
            className="w-full sm:w-auto text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50 px-4 sm:px-6"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Question
          </Button>
          <Button 
            onClick={onMarkAsDuplicate} 
            variant="outline"
            className="w-full sm:w-auto text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 px-4 sm:px-6"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Mark as Duplicate
          </Button>
        </div>
      </div>
    </div>
  );
}